/*
 * EverDriven brand theme for the developer portal.
 *
 * Cloned from the live client site https://www.everdriven.com/ per the brand
 * spec at ~/code/EverDriven/.fleet/idp-assessment/BRAND-SPEC.md. Every palette
 * value below traces back to an extracted brand color; see the spec for the
 * source-of-truth column and the WCAG contrast table.
 *
 * The light scheme is the primary deliverable, extracted from the site. The
 * dark scheme is derived from the brand navy and blue (the site ships no dark
 * mode) and is included to satisfy the fleet dark-mode requirement.
 */
import {
  createUnifiedTheme,
  defaultTypography,
  genPageTheme,
  palettes,
  shapes,
} from '@backstage/theme';
import type { BackstageTypography, UnifiedTheme } from '@backstage/theme';

// Montserrat carries both body and headings on the site. Arvo is the slab-serif
// accent, applied per component where a heading should read as an accent; the
// base family stays Montserrat.
const fontFamily = '"Montserrat", "Helvetica Neue", Arial, sans-serif';

// Keep the Backstage default numeric scale (font sizes and margins) and only
// swap the family and lift heading weights to 600/700, which matches the site
// (Montserrat 400 body, 600 to 700 headings and buttons). The site exposes no
// single modular scale, so no hand-authored scale is introduced here.
const typography: BackstageTypography = {
  ...defaultTypography,
  fontFamily,
  h1: { ...defaultTypography.h1, fontFamily, fontWeight: 700 },
  h2: { ...defaultTypography.h2, fontFamily, fontWeight: 700 },
  h3: { ...defaultTypography.h3, fontFamily, fontWeight: 600 },
  h4: { ...defaultTypography.h4, fontFamily, fontWeight: 600 },
  h5: { ...defaultTypography.h5, fontFamily, fontWeight: 600 },
  h6: { ...defaultTypography.h6, fontFamily, fontWeight: 600 },
};

export const everdrivenLight: UnifiedTheme = createUnifiedTheme({
  fontFamily,
  typography,
  palette: {
    ...palettes.light,
    primary: { main: '#003459', light: '#1B6EC8', dark: '#00243D' },
    // secondary is bright blue (5.11:1 on white). Gold is reserved as a
    // background-only accent, so it is not the MUI secondary text color.
    secondary: { main: '#1B6EC8', light: '#4C8FD6', dark: '#003459' },
    error: { main: '#D9534F' },
    warning: { main: '#F0AD4E' },
    success: { main: '#5CB85C' },
    info: { main: '#1B6EC8' },
    background: {
      default: '#F1F3F4',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1F2124',
      secondary: '#69727D',
    },
    // Backstage-specific palette keys
    navigation: {
      background: '#003459',
      indicator: '#F7C72D', // gold selected indicator on navy, 8.05:1
      color: '#D7E2EA', // resting nav label, light on navy
      selectedColor: '#FFFFFF',
      navItem: { hoverBackground: '#00243D' },
      submenu: { background: '#00243D' },
    },
    banner: {
      info: '#1B6EC8',
      error: '#D9534F',
      text: '#FFFFFF',
      link: '#F7C72D',
    },
  },
  defaultPageTheme: 'home',
  pageTheme: {
    home: genPageTheme({
      colors: ['#003459', '#1B6EC8'],
      shape: shapes.wave,
      options: { fontColor: '#FFFFFF' },
    }),
    documentation: genPageTheme({
      colors: ['#003459', '#26C4B5'],
      shape: shapes.wave2,
      options: { fontColor: '#FFFFFF' },
    }),
    tool: genPageTheme({
      colors: ['#003459', '#1B6EC8'],
      shape: shapes.round,
      options: { fontColor: '#FFFFFF' },
    }),
    service: genPageTheme({
      colors: ['#003459', '#1B6EC8'],
      shape: shapes.wave,
      options: { fontColor: '#FFFFFF' },
    }),
    website: genPageTheme({
      colors: ['#1B6EC8', '#26C4B5'],
      shape: shapes.wave,
      options: { fontColor: '#FFFFFF' },
    }),
    library: genPageTheme({
      colors: ['#003459', '#26C4B5'],
      shape: shapes.wave2,
      options: { fontColor: '#FFFFFF' },
    }),
    other: genPageTheme({
      colors: ['#003459', '#1B6EC8'],
      shape: shapes.wave,
      options: { fontColor: '#FFFFFF' },
    }),
    app: genPageTheme({
      colors: ['#003459', '#1B6EC8'],
      shape: shapes.wave,
      options: { fontColor: '#FFFFFF' },
    }),
    apis: genPageTheme({
      colors: ['#003459', '#26C4B5'],
      shape: shapes.round,
      options: { fontColor: '#FFFFFF' },
    }),
  },
});

export const everdrivenDark: UnifiedTheme = createUnifiedTheme({
  fontFamily,
  typography,
  palette: {
    ...palettes.dark,
    // primary is lifted from the navy because #003459 has no contrast on a dark
    // surface; the status greens and reds are lightened for legibility on dark
    // paper. Re-check each derived value against 4.5:1 during visual review.
    primary: { main: '#4C8FD6', light: '#7FB0E4', dark: '#1B6EC8' },
    secondary: { main: '#26C4B5' },
    error: { main: '#E57373' },
    warning: { main: '#F0AD4E' },
    success: { main: '#6DBE71' },
    info: { main: '#4C8FD6' },
    background: {
      default: '#0A1E30', // deep navy, darker than the brand primary
      paper: '#12283D',
    },
    text: {
      primary: '#E8EEF3',
      secondary: '#9FB2C2',
    },
    navigation: {
      background: '#00243D',
      indicator: '#F7C72D',
      color: '#9FB2C2',
      selectedColor: '#FFFFFF',
      navItem: { hoverBackground: '#0A1E30' },
      submenu: { background: '#0A1E30' },
    },
    banner: {
      info: '#4C8FD6',
      error: '#E57373',
      text: '#FFFFFF',
      link: '#F7C72D',
    },
  },
  defaultPageTheme: 'home',
  // The spec supplies only the dark home gradient. The remaining page kinds are
  // filled from the same brand pairs on the dark navy base (#00243D) mirroring
  // the light shape assignments, so every page kind reads on-brand in dark mode.
  pageTheme: {
    home: genPageTheme({
      colors: ['#00243D', '#1B6EC8'],
      shape: shapes.wave,
      options: { fontColor: '#FFFFFF' },
    }),
    documentation: genPageTheme({
      colors: ['#00243D', '#26C4B5'],
      shape: shapes.wave2,
      options: { fontColor: '#FFFFFF' },
    }),
    tool: genPageTheme({
      colors: ['#00243D', '#1B6EC8'],
      shape: shapes.round,
      options: { fontColor: '#FFFFFF' },
    }),
    service: genPageTheme({
      colors: ['#00243D', '#1B6EC8'],
      shape: shapes.wave,
      options: { fontColor: '#FFFFFF' },
    }),
    website: genPageTheme({
      colors: ['#1B6EC8', '#26C4B5'],
      shape: shapes.wave,
      options: { fontColor: '#FFFFFF' },
    }),
    library: genPageTheme({
      colors: ['#00243D', '#26C4B5'],
      shape: shapes.wave2,
      options: { fontColor: '#FFFFFF' },
    }),
    other: genPageTheme({
      colors: ['#00243D', '#1B6EC8'],
      shape: shapes.wave,
      options: { fontColor: '#FFFFFF' },
    }),
    app: genPageTheme({
      colors: ['#00243D', '#1B6EC8'],
      shape: shapes.wave,
      options: { fontColor: '#FFFFFF' },
    }),
    apis: genPageTheme({
      colors: ['#00243D', '#26C4B5'],
      shape: shapes.round,
      options: { fontColor: '#FFFFFF' },
    }),
  },
});
